import { Component, Input } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { finalize } from 'rxjs';
import { ContractService, ManualInvoiceBody } from '../../../services/contract.service';

/**
 * Super-Admin: manuelle Rechnung als Entwurf (PRD custom-contracts, Phase 3).
 * Positionen netto in EUR; ausgestellt und versendet wird erst mit der
 * Freigabe in der Rechnungsliste. Schliesst mit der angelegten Rechnung.
 */
@Component({
  selector: 'app-manual-invoice-dialog',
  templateUrl: './manual-invoice-dialog.component.html',
  styleUrl: './manual-invoice-dialog.component.scss',
})
export class ManualInvoiceDialogComponent {
  @Input() clientId!: string;
  @Input() paymentTermsDays = 14;

  saving = false;
  form: FormGroup;

  constructor(
    protected dialogRef: NbDialogRef<ManualInvoiceDialogComponent>,
    protected fb: FormBuilder,
    protected contractService: ContractService
  ) {
    this.form = this.fb.group({
      lines: this.fb.array([this.newLine()]),
      service_start: [''],
      service_end: [''],
      payment_terms_days: [null as number | null, [Validators.min(0), Validators.max(180)]],
      note: ['', Validators.maxLength(2000)],
    });
  }

  get lines(): FormArray {
    return this.form.get('lines') as FormArray;
  }

  /** Summe netto in Cent fuer die Anzeige. */
  get totalCents(): number {
    return this.lines.controls.reduce((sum, line) => {
      const quantity = Number(line.get('quantity')!.value) || 0;
      const price = Math.round((Number(line.get('unit_price_eur')!.value) || 0) * 100);
      return sum + quantity * price;
    }, 0);
  }

  private newLine(): FormGroup {
    return this.fb.group({
      description: ['', [Validators.required, Validators.maxLength(500)]],
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(100000)]],
      unit: ['', Validators.maxLength(20)], // alphacount limit
      unit_price_eur: [null as number | null, [Validators.required, Validators.min(0)]],
    });
  }

  addLine(): void {
    this.lines.push(this.newLine());
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) {
      this.lines.removeAt(index);
    }
  }

  showInputError(control: AbstractControl | null): boolean {
    return !!control && control.touched && control.invalid;
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.saving) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const terms = v.payment_terms_days;
    const body: ManualInvoiceBody = {
      lines: v.lines.map((line: any) => ({
        description: String(line.description).trim(),
        quantity: Math.round(Number(line.quantity)),
        unit: (line.unit || '').trim() || null,
        unit_price_net_cents: Math.round(Number(line.unit_price_eur) * 100),
      })),
      service_start: v.service_start || null,
      service_end: v.service_end || null,
      payment_terms_days: terms === null || terms === '' ? null : Number(terms),
      note: (v.note || '').trim() || null,
    };
    this.saving = true;
    this.contractService
      .createManualInvoice(this.clientId, body)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (invoice) => this.dialogRef.close(invoice),
        // Fehlertext kommt aus dem Error-Interceptor.
        error: () => undefined,
      });
  }
}
